export type ProblemThrottleState = {
  lastSentAt: number
  suppressedCount: number
}

export const APPLICATION_PROBLEM_WATCHER_DEFAULT_THROTTLE_MINUTES = 30
export const APPLICATION_PROBLEM_WATCHER_MIN_THROTTLE_MINUTES = 15
export const APPLICATION_PROBLEM_WATCHER_MAX_THROTTLE_MINUTES = 60
export const APPLICATION_PROBLEM_WATCHER_DEFAULT_FREEZE_THRESHOLD_MS = 1500
export const APPLICATION_PROBLEM_WATCHER_HEARTBEAT_INTERVAL_MS = 1000
export const APPLICATION_PROBLEM_WATCHER_VERSION = '2026-10-11'

const MAX_MESSAGE_LENGTH = 180
const TOKEN_PATTERN = /\b[A-Za-z0-9_-]{32,}\b/g
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const OPTIONAL_BROWSER_BRIDGE_ERROR_PATTERNS = [
  /^Error invoking postMessage: Java object is gone$/i
]
const OPTIONAL_MEASUREMENT_RESOURCE_HOSTS = new Set([
  'connect.facebook.net',
  'static.klaviyo.com',
  'static-tracking.klaviyo.com',
  'www.googletagmanager.com'
])

const OPTIONAL_MEASUREMENT_RESOURCE_PATHS = [
  /^\/en_US\/fbevents\.js$/,
  /^\/gtag\/js$/,
  /^\/gtm\.js$/,
  /^\/onsite\/js\//,
  /^\/signals\/config\//
]

export const normalizeProblemWatcherThrottleMinutes = (value: unknown) => {
  const numericValue = typeof value === 'number'
    ? value
    : typeof value === 'string'
      ? Number.parseFloat(value)
      : Number.NaN

  if (!Number.isFinite(numericValue)) {
    return APPLICATION_PROBLEM_WATCHER_DEFAULT_THROTTLE_MINUTES
  }

  return Math.min(
    APPLICATION_PROBLEM_WATCHER_MAX_THROTTLE_MINUTES,
    Math.max(APPLICATION_PROBLEM_WATCHER_MIN_THROTTLE_MINUTES, numericValue)
  )
}

export const normalizeProblemWatcherFreezeThresholdMs = (value: unknown) => {
  const numericValue = typeof value === 'number'
    ? value
    : typeof value === 'string'
      ? Number.parseFloat(value)
      : Number.NaN

  if (!Number.isFinite(numericValue)) {
    return APPLICATION_PROBLEM_WATCHER_DEFAULT_FREEZE_THRESHOLD_MS
  }

  return Math.min(10_000, Math.max(500, numericValue))
}

export const sanitizeProblemMessage = (value: unknown) => {
  const rawMessage = value instanceof Error
    ? value.message
    : typeof value === 'string'
      ? value
      : value === null || value === undefined
        ? 'unknown_error'
        : Object.prototype.toString.call(value)

  const sanitized = rawMessage
    .replace(EMAIL_PATTERN, '[email]')
    .replace(TOKEN_PATTERN, '[token]')
    .replace(/\s+/g, ' ')
    .trim()

  if (!sanitized) return 'unknown_error'
  if (sanitized.length <= MAX_MESSAGE_LENGTH) return sanitized
  return `${sanitized.slice(0, MAX_MESSAGE_LENGTH - 1)}...`
}

export const shouldIgnoreApplicationError = (
  reason: unknown,
  message: unknown
) => {
  if (reason !== 'uncaught_exception' && reason !== 'unhandled_promise_rejection') {
    return false
  }

  const sanitizedMessage = sanitizeProblemMessage(message)
  return OPTIONAL_BROWSER_BRIDGE_ERROR_PATTERNS.some(pattern => pattern.test(sanitizedMessage))
}

export const sanitizeProblemSourceUrl = (sourceUrl: unknown, currentOrigin = '') => {
  if (typeof sourceUrl !== 'string' || !sourceUrl) {
    return {
      source_origin: 'unknown',
      source_host: null,
      source_path: null
    }
  }

  try {
    const parsedUrl = new URL(sourceUrl, currentOrigin || undefined)
    const sourceOrigin = currentOrigin && parsedUrl.origin === currentOrigin
      ? 'same_origin'
      : 'external'
    const sourceHost = sourceOrigin === 'same_origin'
      ? 'same_origin'
      : parsedUrl.hostname.slice(0, 80)

    return {
      source_origin: sourceOrigin,
      source_host: sourceHost || null,
      source_path: parsedUrl.pathname.slice(0, 140)
    }
  } catch {
    return {
      source_origin: 'unknown',
      source_host: null,
      source_path: null
    }
  }
}

export const shouldIgnoreResourceLoadFailure = (
  resourceTag: unknown,
  source: ReturnType<typeof sanitizeProblemSourceUrl>
) => {
  if (resourceTag !== 'script') return false
  if (source.source_origin !== 'external') return false
  if (!source.source_host || !source.source_path) return false
  if (!OPTIONAL_MEASUREMENT_RESOURCE_HOSTS.has(source.source_host)) return false

  return OPTIONAL_MEASUREMENT_RESOURCE_PATHS.some(pattern => pattern.test(source.source_path || ''))
}

const sanitizeBucketPart = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'unknown'

  return String(value)
    .replace(/[^a-zA-Z0-9._/-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 90) || 'unknown'
}

export const createProblemEventBucket = (
  eventName: string,
  alertBucket: string,
  parts: unknown[] = []
) => {
  return [eventName, alertBucket, ...parts]
    .map(sanitizeBucketPart)
    .join(':')
    .slice(0, 220)
}

export const createProblemEventThrottle = (throttleMs: number) => {
  const states = new Map<string, ProblemThrottleState>()

  return (bucket: string, now = Date.now()) => {
    const state = states.get(bucket)

    if (state && now - state.lastSentAt < throttleMs) {
      state.suppressedCount += 1
      return {
        shouldSend: false,
        suppressedCount: state.suppressedCount
      }
    }

    const suppressedCount = state?.suppressedCount ?? 0
    states.set(bucket, {
      lastSentAt: now,
      suppressedCount: 0
    })

    return {
      shouldSend: true,
      suppressedCount
    }
  }
}

export const browserFamilyFromUserAgent = (userAgent: string) => {
  if (/Edg\//.test(userAgent)) return 'edge'
  if (/Chrome\//.test(userAgent) && !/Chromium\//.test(userAgent)) return 'chrome'
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) return 'safari'
  if (/Firefox\//.test(userAgent)) return 'firefox'
  return 'unknown'
}
